<template>
    <div class="modal-overlay">
        <div class="modal-content">
            <h3 class="text-2xl font-bold mb-4 flex items-center">
                <font-awesome-icon :icon="['fas', 'info-circle']" class="text-blue-500 mr-2" />
                {{ $translate('account_conversion.title') }}
            </h3>
            <p class="text-gray-600 mb-4">
                {{ $translate('account_conversion.description') }}
            </p>
            <form @submit.prevent="handleSubmit">
                <!-- Email Field -->
                <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div class="md:col-span-12">
                        <label class="block mb-1 font-medium text-gray-700">{{ $translate('Email') }}</label>
                        <input type="email" v-model="user.email"
                            class="block w-full p-3 rounded-md border border-gray-300 text-sm text-gray-900 focus:ring-gray-900 focus:border-gray-900 transition"
                            :class="{ 'bg-gray-200': true }" disabled />
                    </div>
                </div>
                <!-- First and Last Name Fields -->
                <div class="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
                    <div class="md:col-span-6">
                        <label class="block mb-1 font-medium text-gray-700">{{ $translate('First Name') }} <span
                                class="text-red-500 ml-1">*</span></label>
                        <input type="text" v-model="user.first_name"
                            class="block w-full p-3 rounded-md border border-gray-300 bg-white text-sm text-gray-900 focus:ring-gray-900 focus:border-gray-900 transition" />
                    </div>
                    <div class="md:col-span-6">
                        <label class="block mb-1 font-medium text-gray-700">{{ $translate('Last Name') }} <span
                                class="text-red-500 ml-1">*</span></label>
                        <input type="text" v-model="user.last_name"
                            class="block w-full p-3 rounded-md border border-gray-300 bg-white text-sm text-gray-900 focus:ring-gray-900 focus:border-gray-900 transition" />
                    </div>
                </div>
                <!-- Password Fields -->
                <div class="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
                    <div class="md:col-span-6">
                        <label class="block mb-1 font-medium text-gray-700">{{ $translate('Password') }} <span
                                class="text-red-500 ml-1">*</span></label>
                        <input type="password" v-model="password"
                            class="block w-full p-3 rounded-md border border-gray-300 bg-white text-sm text-gray-900 focus:ring-gray-900 focus:border-gray-900 transition" />
                    </div>
                    <div class="md:col-span-6">
                        <label class="block mb-1 font-medium text-gray-700">{{ $translate('Password Confirmation') }}
                            <span class="text-red-500 ml-1">*</span></label>
                        <input type="password" v-model="verifyPassword"
                            class="block w-full p-3 rounded-md border border-gray-300 bg-white text-sm text-gray-900 focus:ring-gray-900 focus:border-gray-900 transition" />
                    </div>
                </div>
                <!-- Terms and Privacy Agreement -->
                <div class="my-3 space-x-2">
                    <input type="checkbox" id="termsAndPrivacy" v-model="agreed"
                        class="h-4 w-4 text-gray-700 border-gray-300 rounded" />
                    <label for="termsAndPrivacy" class="text-sm font-medium text-gray-700">
                        {{ $translate('I agree to') }}
                        <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('terms') } }"
                            class="text-blue-600 hover:text-blue-800">
                            {{ $translate('our Terms & Conditions') }}
                        </router-link>
                        {{ $translate('and with') }}
                        <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('privacy') } }"
                            class="text-blue-600 hover:text-blue-800">
                            {{ $translate('our Privacy Policy') }}
                        </router-link>.<span class="text-red-500 ml-1">*</span>
                    </label>
                </div>
                <!-- Action Buttons -->
                <div class="flex justify-between items-center mt-4">
                    <!-- Continue as Link -->
                    <button type="button" @click="$emit('close')"
                        class="text-gray-500 hover:text-gray-700 font-medium focus:outline-none">
                        {{ $translate('account_conversion.continue') }}
                    </button>
                    <!-- Register Button -->
                    <button type="submit"
                        class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center">
                        {{ $translate('account_conversion.register') }} <font-awesome-icon
                            :icon="['fas', 'arrow-right']" class="ml-2" />
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>


<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { notify } from "notiwind";
import { translate } from '@/utils/translate';
import { getUserData, setUserData, cookie } from '@/utils/cookie';

export default {
    name: 'AccountConversionModal',
    setup() {
        const router = useRouter();
        const password = ref('');
        const verifyPassword = ref('');
        const agreed = ref(false);
        const user = getUserData();
        const session_id = cookie();
        console.log(user);
        const handleSubmit = () => {
            if (!agreed.value) {
                notify({ group: "error", title: translate('Error'), text: translate('Please agree to the terms.') }, 15000);
                return;
            }

            if (password.value.length < 6) {
                notify({ group: "error", title: translate('Error'), text: translate('Password must be at least 6 characters long.') }, 15000);
                return;
            }

            if (verifyPassword.value !== password.value) {
                notify({ group: "error", title: translate('Error'), text: translate('Passwords do not match.') }, 15000);
                return;
            }
            if (!user.first_name || !user.last_name) {
                notify({ group: "error", title: translate('Error'), text: translate('First and last name are required.') }, 15000);
                return;
            }

            // Prepare data for API call
            const data = {
                password: password.value,
                session_id: session_id,
                brand_id: window.vueAppConfig.info.id,
                first_name: user.first_name,
                last_name: user.last_name,
            };

            // Call the API to convert the account
            fetch(`${window.vueAppConfig.endpoint}/api/v2/session/convert-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data)
            })
                .then(response => {
                    if (!response.ok) throw new Error('Network response was not ok');
                    return response.json();
                })
                .then(data => {
                    if (data.status) {
                        if (data.message) {
                            notify({ type: "info", group: "info", title: translate('Success'), text: data.message }, 10000);
                        }
                        if (data.user) {
                            setUserData(data.user);
                        }
                        // Close modal and reload the page or redirect as needed
                        router.go(); // Reload the current page
                    } else {
                        notify({ group: "error", title: translate('Error'), text: data.message }, 15000);
                    }
                })
                .catch(error => {
                    console.error('Error converting account:', error);
                    notify({ group: "error", title: translate('Error'), text: translate('An error occurred. Please try again later.') }, 15000);
                });
        };

        return {
            password,
            verifyPassword,
            agreed,
            handleSubmit,
            user,
        };
    },
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    max-width: 500px;
    width: 100%;
}
</style>